import { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import { LuPrinter } from "react-icons/lu";
import DialogTitleWithClose from "../../components/DialogTitleWithClose";
import { previewInvoice } from "../../services/invoice";
import { parseApiError } from "../../utils/api-error";
import { formatMoney } from "../../utils/format";
import { errorMsg, successMsg } from "../../utils/toast-msg";
import { generateAndPrintInvoices } from "./invoiceDocument";
import dashboardStyles from "../../styles/dashboard.module.css";

const ROWS = [
  ["Product", "productTotal"],
  ["CN Courier", "cnCourier"],
  ["Shipping", "shipping"],
  ["BD Courier", "bdCourier"],
  ["Discount", "discount", "minus"],
];

// The summary shown BEFORE an invoice is generated: who it is for, which orders and what it adds up to. Nothing is
// generated (no invoice no is taken) until "Generate & Print" is pressed here.
const InvoiceSummaryDialog = ({ orderIds, separate = false, onClose, onDone }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const open = Boolean(orderIds?.length);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setSummary(null);
    setLoading(true);
    previewInvoice(orderIds)
      .then((data) => !cancelled && setSummary(data))
      .catch((error) => {
        if (cancelled) return;
        errorMsg(parseApiError(error, "Could not load the invoice summary").generalMessage);
        onClose();
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderIds]);

  const generate = async () => {
    setWorking(true);
    try {
      const invoices = await generateAndPrintInvoices(orderIds, { separate });
      successMsg(`${invoices.map((invoice) => invoice.invoiceNo).join(", ")} generated and sent to print`);
      onDone?.(invoices);
      onClose();
    } catch (error) {
      errorMsg(parseApiError(error, "Could not generate the invoice").generalMessage);
    } finally {
      setWorking(false);
    }
  };

  const line = (label, value, { strong, color } = {}) => (
    <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontWeight: strong ? 700 : 400, color, borderTop: strong ? "1px solid var(--color-border, #e5e7eb)" : undefined }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );

  return (
    <Dialog open={open} onClose={working ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithClose onClose={onClose} disabled={working}>
        Generate Invoice
      </DialogTitleWithClose>
      <DialogContent sx={{ fontSize: 14 }}>
        {loading || !summary ? (
          <p style={{ color: "var(--color-text-muted)" }}>Loading summary...</p>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, letterSpacing: 0.6, color: "var(--color-text-muted)" }}>BILL TO</div>
              <div style={{ fontWeight: 700 }}>{summary.customerName || "-"}</div>
              <div style={{ color: "var(--color-text-muted)" }}>{[summary.customerCode, summary.customerPhone].filter(Boolean).join(" | ")}</div>
            </div>

            <div style={{ display: "flex", gap: 24, marginBottom: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>Orders</div>
                <strong>{summary.orderCount}</strong>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>Quantity</div>
                <strong>{summary.quantity}</strong>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>Weight</div>
                <strong>{summary.weight}</strong>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>Delivery</div>
                <strong>{summary.deliveryMethod || "-"}</strong>
              </div>
            </div>

            <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 12, wordBreak: "break-word" }}>{(summary.orderCodes || []).join(", ")}</div>

            <div style={{ border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 8, padding: "6px 12px" }}>
              {ROWS.filter(([, key, kind]) => kind !== "minus" || +summary[key]).map(([label, key, kind]) => line(label, `${kind === "minus" ? "- " : ""}${formatMoney(summary[key])}`))}
              {+summary.other ? line("Other", formatMoney(summary.other)) : null}
              {line("Total", formatMoney(summary.total), { strong: true })}
              {line("Paid", formatMoney(summary.paid), { color: "#15803d" })}
              {line("Due", formatMoney(summary.due), { strong: true, color: "#dc2626" })}
            </div>

            <p style={{ marginTop: 12, marginBottom: 0, fontSize: 12, color: "var(--color-text-muted)" }}>
              {separate && orderIds.length > 1
                ? `${orderIds.length} separate invoices (one per order) will be generated, each with its own invoice no.`
                : "Generating gives this invoice its invoice no and saves it in the Invoice History."}
            </p>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <button type="button" className={`${dashboardStyles.btn} ${dashboardStyles.btnOutline}`} onClick={onClose} disabled={working}>
          Cancel
        </button>
        <button type="button" className={`${dashboardStyles.btn} ${dashboardStyles.btnPrimary}`} onClick={generate} disabled={working || loading || !summary}>
          <LuPrinter />
          {working ? "Generating..." : "Generate & Print"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceSummaryDialog;
